import { FormEvent, useEffect, useRef, useState } from 'react';

import { Pencil, Search, X } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { TooltipProvider } from 'components/Tooltip';
import { Input } from 'components/input/Input';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  CloneJobStatus,
  LoadCloneRecommendation,
  LoadCloneRecommendationDisposition,
  LoadCloneRecommendationSource,
  getLoadCloneRecommendationSources,
  updateLoadCloneRecommendation,
} from 'lib/api/cloneLoads';
import InstructionsDialog from 'pages/LoadView/Cloning/InstructionsDialog';
import TaskCard, { CloneEntryPoint } from 'pages/LoadView/Cloning/TaskCard';
import { isActiveCloneJob } from 'pages/LoadView/loadClonePolling';
import {
  LoadCloneJobStateStatus,
  useLoadCloneJob,
} from 'pages/LoadView/useLoadCloneJob';
import { Maybe } from 'types/UtilityTypes';

type Props = {
  recommendation: LoadCloneRecommendation;
  onChanged: (recommendation?: LoadCloneRecommendation) => void;
  onRecommendationConflict?: () => Promise<boolean>;
  onOpenLoad: (loadIdentifier: string) => void;
  onUseBuilder: () => void;
  onJobStatusChanged?: (id: number, status: CloneJobStatus) => void;
  allowBuilder?: boolean;
  reconciliationBlocked?: boolean;
};

export default function LoadCloneRecommendationCard({
  recommendation,
  onChanged,
  onRecommendationConflict,
  onOpenLoad,
  onUseBuilder,
  onJobStatusChanged,
  allowBuilder = true,
  reconciliationBlocked = false,
}: Props) {
  const posthog = usePostHog();
  const [localRecommendation, setLocalRecommendation] =
    useState(recommendation);
  const [source, setSource] = useState(recommendation.source);
  const [isChangingSource, setIsChangingSource] = useState(!source);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LoadCloneRecommendationSource[]>([]);
  const [searchState, setSearchState] = useState<
    'idle' | 'loading' | 'empty' | 'error'
  >('idle');
  const [actionError, setActionError] = useState<Maybe<string>>(null);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const requestRef = useRef(0);
  const mutationRef = useRef(false);
  const mountedRef = useRef(true);
  const previousRecommendationRef = useRef(recommendation);
  const recommendationRef = useRef(recommendation);
  const shownRecommendationIdRef = useRef<Maybe<number>>(null);
  recommendationRef.current = recommendation;
  const started =
    localRecommendation.disposition ===
    LoadCloneRecommendationDisposition.Started;

  useEffect(() => {
    const previous = previousRecommendationRef.current;
    previousRecommendationRef.current = recommendation;
    setLocalRecommendation(recommendation);
    setSource(recommendation.source);

    // Polling refreshes objects without changing the recommendation. Preserve
    // the open picker and in-flight search unless its underlying choice changed.
    if (
      previous.id === recommendation.id &&
      previous.version === recommendation.version &&
      previous.source?.loadId === recommendation.source?.loadId &&
      previous.customerName === recommendation.customerName &&
      previous.disposition === recommendation.disposition &&
      previous.latestJobId === recommendation.latestJobId
    )
      return;

    requestRef.current += 1;
    setIsChangingSource(!recommendation.source);
    setActionError(null);
    setResults([]);
    setSearchState('idle');
  }, [recommendation]);

  useEffect(() => {
    if (!posthog || shownRecommendationIdRef.current === recommendation.id)
      return;
    shownRecommendationIdRef.current = recommendation.id;
    posthog.capture('load_clone_recommendation_shown', {
      recommendation_id: recommendation.id,
      has_source: Boolean(recommendation.source),
      disposition: recommendation.disposition,
    });
  }, [
    posthog,
    recommendation.id,
    recommendation.source,
    recommendation.disposition,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  const cloneJob = useLoadCloneJob({
    enabled: Boolean(source),
    sourceLoadId: source?.loadId,
    emailId: recommendation.emailId,
    recommendationId: recommendation.id,
    recommendationVersion: localRecommendation.version,
    recommendationStarted: started,
    latestJobId: recommendation.latestJobId,
    onStarted: onChanged,
    onRecommendationConflict,
  });

  const jobStatus =
    cloneJob.state.status === LoadCloneJobStateStatus.Job
      ? cloneJob.state.job.status
      : undefined;
  useEffect(() => {
    if (jobStatus) onJobStatusChanged?.(recommendation.id, jobStatus);
  }, [jobStatus, onJobStatusChanged, recommendation.id]);

  const jobBlocksActions =
    cloneJob.state.status === LoadCloneJobStateStatus.Discovering ||
    cloneJob.state.status === LoadCloneJobStateStatus.Starting ||
    cloneJob.state.status ===
      LoadCloneJobStateStatus.RefreshingRecommendation ||
    cloneJob.state.status ===
      LoadCloneJobStateStatus.RecommendationReconciliationError ||
    cloneJob.state.status === LoadCloneJobStateStatus.StartedWithoutJob ||
    (cloneJob.state.status === LoadCloneJobStateStatus.Job &&
      isActiveCloneJob(cloneJob.state.job));
  const actionsDisabled =
    started || jobBlocksActions || isMutating || reconciliationBlocked;
  const startClone = async () => {
    if (
      jobBlocksActions ||
      isMutating ||
      reconciliationBlocked ||
      mutationRef.current
    )
      return false;
    return cloneJob.start();
  };

  const showActionError = (message: string, stale?: boolean) => {
    setActionError(
      stale
        ? 'This recommendation changed elsewhere. Reloading the latest version.'
        : message
    );
    if (stale) void cloneJob.handleRecommendationConflict();
  };

  const search = async (event: FormEvent) => {
    event.preventDefault();
    const loadNumber = query.trim();
    if (!loadNumber) return;
    const request = ++requestRef.current;
    setSearchState('loading');
    setResults([]);
    const result = await getLoadCloneRecommendationSources(
      recommendation.id,
      loadNumber
    );
    if (!mountedRef.current || request !== requestRef.current) return;
    if (result.isErr()) {
      setSearchState('error');
      return;
    }
    setResults(result.value);
    setSearchState(result.value.length ? 'idle' : 'empty');
  };

  const selectSource = async (next: LoadCloneRecommendationSource) => {
    if (actionsDisabled || mutationRef.current) return;
    mutationRef.current = true;
    setIsMutating(true);
    setActionError(null);
    const binding = {
      id: recommendation.id,
      threadId: recommendation.threadId,
      version: localRecommendation.version,
      sourceLoadId: recommendation.source?.loadId,
    };
    const result = await updateLoadCloneRecommendation(recommendation.id, {
      version: localRecommendation.version,
      sourceLoadId: next.loadId,
    });
    mutationRef.current = false;
    if (mountedRef.current) setIsMutating(false);
    if (!mountedRef.current) return;
    const current = recommendationRef.current;
    if (
      current.id !== binding.id ||
      current.threadId !== binding.threadId ||
      current.version !== binding.version ||
      current.source?.loadId !== binding.sourceLoadId
    )
      return;
    if (result.isErr()) {
      showActionError(result.error.message, result.error.status === 409);
      return;
    }
    setLocalRecommendation(result.value);
    setSource(result.value.source ?? next);
    setIsChangingSource(false);
    setResults([]);
    posthog?.capture('load_clone_recommendation_source_selected', {
      recommendation_id: recommendation.id,
      source_load_id: next.loadId,
      changed: Boolean(recommendation.source),
    });
    onChanged(result.value);
  };

  const cancelSourceChange = () => {
    requestRef.current += 1;
    setIsChangingSource(false);
    setResults([]);
    setSearchState('idle');
    setActionError(null);
  };

  const dismissRecommendation = async () => {
    if (actionsDisabled || mutationRef.current) return;
    mutationRef.current = true;
    setIsMutating(true);
    setActionError(null);
    const binding = {
      id: recommendation.id,
      threadId: recommendation.threadId,
      version: localRecommendation.version,
      sourceLoadId: recommendation.source?.loadId,
    };
    const result = await updateLoadCloneRecommendation(recommendation.id, {
      version: localRecommendation.version,
      disposition: LoadCloneRecommendationDisposition.Dismissed,
    });
    mutationRef.current = false;
    if (mountedRef.current) setIsMutating(false);
    const current = recommendationRef.current;
    if (
      !mountedRef.current ||
      current.id !== binding.id ||
      current.threadId !== binding.threadId ||
      current.version !== binding.version ||
      current.source?.loadId !== binding.sourceLoadId
    )
      return;
    if (result.isErr()) {
      showActionError(result.error.message, result.error.status === 409);
      return;
    }
    setLocalRecommendation(result.value);
    posthog?.capture('load_clone_recommendation_dismissed', {
      recommendation_id: recommendation.id,
    });
    onChanged(result.value);
  };

  const chooseBuilder = () => {
    if (mutationRef.current) return;
    posthog?.capture('load_clone_recommendation_builder_chosen', {
      recommendation_id: recommendation.id,
    });
    onUseBuilder();
    // Ordinary suggestions already exist, so the builder opens without waiting. Recording the
    // choice only retires this recommendation; a failure leaves it pending for later.
    void updateLoadCloneRecommendation(recommendation.id, {
      version: localRecommendation.version,
      disposition: LoadCloneRecommendationDisposition.BuilderChosen,
    }).then((result) => {
      if (result.isOk()) onChanged(result.value);
    });
  };

  if (
    recommendation.disposition ===
      LoadCloneRecommendationDisposition.Dismissed ||
    recommendation.disposition ===
      LoadCloneRecommendationDisposition.BuilderChosen
  ) {
    return null;
  }

  const idlePresentation = source ? (
    <div className='space-y-2'>
      <CloneEntryPoint
        discovering={actionsDisabled}
        onClick={() => void startClone()}
        onAddInstructions={() => setIsInstructionsOpen(true)}
      />
      {cloneJob.state.status === 'idle' && cloneJob.state.startError && (
        <Typography variant='body-xs' className='text-error-600' role='alert'>
          {cloneJob.state.startError.message}
        </Typography>
      )}
      <InstructionsDialog
        sourceLoadId={source.loadId}
        emailId={recommendation.emailId}
        open={isInstructionsOpen}
        startError={
          cloneJob.state.status === 'idle'
            ? cloneJob.state.startError
            : undefined
        }
        onOpenChange={setIsInstructionsOpen}
        onStart={startClone}
      />
    </div>
  ) : null;

  return (
    <TooltipProvider>
      <Card className='mt-3 border-brand-main/25 bg-brand-50 p-3 shadow-none'>
        <Flex justify='between' align='start' className='gap-3'>
          <div className='min-w-0'>
            <Typography variant='body-sm' weight='semibold'>
              {isChangingSource
                ? 'Choose a source load'
                : 'Clone from this load'}
            </Typography>
            <Typography
              variant='body-xs'
              textColor='lessMuted'
              className='mt-0.5'
            >
              {isChangingSource
                ? 'Find the previous load to use for this email.'
                : 'Use this email’s details. Keep the original unchanged.'}
            </Typography>
          </div>
          {!actionsDisabled && (
            <button
              type='button'
              aria-label={
                isChangingSource && source
                  ? 'Cancel source change'
                  : 'Dismiss clone recommendation'
              }
              className='rounded p-1 text-neutral-500 hover:bg-neutral-100'
              onClick={() => {
                if (isChangingSource && source) cancelSourceChange();
                else void dismissRecommendation();
              }}
            >
              <X className='h-4 w-4' />
            </button>
          )}
        </Flex>

        {source && !isChangingSource ? (
          <SourceSummary
            source={source}
            canChange={!actionsDisabled}
            onChange={() => {
              posthog?.capture(
                'load_clone_recommendation_source_change_opened',
                {
                  recommendation_id: recommendation.id,
                }
              );
              setIsChangingSource(true);
            }}
            onOpen={() => onOpenLoad(source.identifier)}
          />
        ) : (
          !actionsDisabled && (
            <div className='mt-3 space-y-2'>
              <form
                className='flex gap-2'
                onSubmit={(event) => void search(event)}
              >
                <label
                  className='sr-only'
                  htmlFor={`clone-source-${recommendation.id}`}
                >
                  Source load number
                </label>
                <Input
                  id={`clone-source-${recommendation.id}`}
                  className='min-w-0 flex-1'
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder='Enter exact load number'
                  autoComplete='off'
                />
                <Button
                  buttonNamePosthog={null}
                  size='sm'
                  type='submit'
                  variant='outline'
                  className='h-8 shrink-0 gap-1.5 px-2.5 text-xs'
                  disabled={searchState === 'loading' || !query.trim()}
                >
                  <Search className='h-3.5 w-3.5' aria-hidden='true' />
                  {searchState === 'loading' ? 'Searching...' : 'Find'}
                </Button>
              </form>
              {searchState === 'empty' && (
                <Typography variant='body-xs' role='status'>
                  No load found with that exact number.
                </Typography>
              )}
              {searchState === 'error' && (
                <Typography
                  variant='body-xs'
                  className='text-error-600'
                  role='alert'
                >
                  Could not search for that load. Try again.
                </Typography>
              )}
              {results.map((result) => (
                <button
                  type='button'
                  key={result.loadId}
                  className='w-full cursor-pointer rounded-md border border-neutral-300 bg-neutral-50 p-3 text-left hover:border-brand-main hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main'
                  disabled={actionsDisabled}
                  onClick={() => void selectSource(result)}
                >
                  <SourceDetails source={result} />
                </button>
              ))}
            </div>
          )
        )}

        {source &&
          (!isChangingSource ||
            cloneJob.state.status ===
              LoadCloneJobStateStatus.RecommendationReconciliationError) && (
            <div className='mt-3'>
              <TaskCard
                {...cloneJob}
                start={startClone}
                sourceLoadId={source.loadId}
                emailId={recommendation.emailId}
                onOpenLoad={onOpenLoad}
                idlePresentation={idlePresentation}
              />
            </div>
          )}

        {((reconciliationBlocked &&
          cloneJob.state.status !==
            LoadCloneJobStateStatus.RecommendationReconciliationError &&
          cloneJob.state.status !==
            LoadCloneJobStateStatus.RefreshingRecommendation) ||
          (!source &&
            cloneJob.state.status ===
              LoadCloneJobStateStatus.RecommendationReconciliationError)) && (
          <Button
            buttonNamePosthog={null}
            variant='ghost'
            onClick={() => {
              if (reconciliationBlocked)
                void cloneJob.handleRecommendationConflict();
              else cloneJob.refresh();
            }}
          >
            Retry recommendation refresh
          </Button>
        )}

        {!source &&
          cloneJob.state.status ===
            LoadCloneJobStateStatus.RefreshingRecommendation && (
            <div className='mt-3'>
              <Typography variant='body-sm' weight='medium' role='status'>
                Refreshing recommendation...
              </Typography>
            </div>
          )}

        {started && !source && (
          <div className='mt-3 rounded-md bg-neutral-50 p-2.5' role='status'>
            <Typography variant='body-sm' weight='medium'>
              Load cloning was started
            </Typography>
            <Typography
              variant='body-xs'
              textColor='lessMuted'
              className='mt-1'
            >
              The saved source or job is no longer available. Check the TMS
              before taking another action.
            </Typography>
          </div>
        )}

        {actionError && (
          <Typography
            variant='body-xs'
            className='mt-2 text-error-600'
            role='alert'
          >
            {actionError}
          </Typography>
        )}

        {allowBuilder && !actionsDisabled && (
          <Button
            buttonNamePosthog={null}
            variant='unstyled'
            size='auto'
            type='button'
            className='mt-3 rounded-sm px-1 py-0.5 text-xs font-medium text-brand-main underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main motion-reduce:transition-none'
            onClick={chooseBuilder}
          >
            Use Load Builder instead
          </Button>
        )}
      </Card>
    </TooltipProvider>
  );
}

function SourceSummary({
  source,
  canChange,
  onChange,
  onOpen,
}: {
  source: LoadCloneRecommendationSource;
  canChange: boolean;
  onChange: () => void;
  onOpen: () => void;
}) {
  return (
    <div className='mt-3 space-y-3'>
      <div>
        <Flex justify='between' align='center' wrap='wrap' gap='sm'>
          <Button
            buttonNamePosthog={null}
            variant='unstyled'
            size='auto'
            type='button'
            aria-label={`View source load ${source.identifier}`}
            className='min-w-0 whitespace-normal break-words rounded-sm px-0 py-0.5 text-sm font-semibold text-brand-main underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main motion-reduce:transition-none'
            onClick={onOpen}
          >
            Load {source.identifier}
          </Button>
          <Flex gap='sm'>
            {canChange && (
              <Button
                buttonNamePosthog={null}
                variant='unstyled'
                size='auto'
                type='button'
                className='shrink-0 gap-1 rounded-sm px-1 py-0.5 text-xs font-medium text-brand-main underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main motion-reduce:transition-none'
                onClick={onChange}
              >
                <Pencil
                  className='h-3 w-3 !stroke-brand-main'
                  aria-hidden='true'
                />{' '}
                Change
              </Button>
            )}
          </Flex>
        </Flex>
        <Typography variant='body-xs' textColor='lessMuted' className='mt-1'>
          {[source.customerName, source.equipment].filter(Boolean).join(' · ')}
        </Typography>
      </div>
      <div className='ml-1 border-l border-dashed border-neutral-300 pl-4'>
        <div className='relative pb-3'>
          <span
            aria-hidden='true'
            className='absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-brand-main'
          />
          <Typography variant='body-xs' textColor='lessMuted'>
            Pickup
          </Typography>
          <Typography variant='body-xs' className='break-words'>
            {source.pickup || 'Not available'}
          </Typography>
        </div>
        <div className='relative'>
          <span
            aria-hidden='true'
            className='absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-brand-main'
          />
          <Typography variant='body-xs' textColor='lessMuted'>
            Delivery
          </Typography>
          <Typography variant='body-xs' className='break-words'>
            {source.delivery || 'Not available'}
          </Typography>
        </div>
      </div>
    </div>
  );
}

function SourceDetails({ source }: { source: LoadCloneRecommendationSource }) {
  return (
    <div className='min-w-0'>
      <Typography variant='body-sm' weight='semibold'>
        Load {source.identifier}
      </Typography>
      <Typography variant='body-xs' textColor='lessMuted' className='mt-0.5'>
        {[source.customerName, source.equipment].filter(Boolean).join(' · ')}
      </Typography>
      <Typography variant='body-xs' className='mt-1'>
        {source.pickup} → {source.delivery}
      </Typography>
    </div>
  );
}
